<template>
  <b-modal
    v-model="isVisible"
    :title="$t('general.videoPreview')"
    hide-footer
    centered
    size="lg"
    content-class="video-preview-modal-content"
    @hidden="onHidden"
    @shown="onShown"
  >
    <div class="video-preview-modal">
      <div class="video-container">
        <video
          v-if="videoUrl && !loadError"
          ref="videoPlayer"
          class="video-player"
          controls
          autoplay
          @error="handleVideoError"
        >
          <source :src="videoUrl" :type="videoType">
          {{ $t('general.videoNotSupported') }}
        </video>
        <div v-else-if="loadError" class="error-message">
          <feather-icon icon="AlertTriangleIcon" size="40" />
          <p>{{ $t('general.videoLoadError') }}</p>
        </div>
        <div v-else class="loading-message">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p>{{ $t('general.loading') }}</p>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'VideoPreviewModal',

  components: {
    BModal,
    BSpinner,
    FeatherIcon,
  },

  props: {
    // Video URL to play
    videoUrl: {
      type: String,
      default: '',
    },
    // Video MIME type
    videoType: {
      type: String,
      default: 'video/mp4',
    },
    // Whether the modal is visible
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadError: false,
    }
  },

  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },

  watch: {
    value(newValue) {
      if (newValue) {
        // Reset state when modal is opened
        this.loadError = false
        if (this.$refs.videoPlayer) {
          this.$refs.videoPlayer.currentTime = 0
        }
      }
    },

    videoUrl() {
      // Reset error state when video URL changes
      this.loadError = false
    },
  },

  methods: {
    onShown() {
      // Focus on the video player when modal is shown
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.focus()
      }
    },

    handleVideoError() {
      this.loadError = true
    },

    onHidden() {
      // Pause video when modal is closed
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
      }
      this.$emit('hidden')
    },
  },
}
</script>

<style lang="scss" scoped>
.video-preview-modal {
  .video-container {
    position: relative;
    width: 100%;
    background-color: #000;
    border-radius: 8px;
    overflow: hidden;

    // 16:9 aspect ratio
    &::before {
      content: "";
      display: block;
      padding-top: 56.25%;
    }

    .video-player {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .error-message {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #f8d7da;
      color: #721c24;

      p {
        margin-top: 1rem;
        font-weight: 500;
      }
    }

    .loading-message {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #f8f9fa;
      color: #6c757d;

      p {
        margin-top: 1rem;
        font-weight: 500;
      }
    }
  }
}
</style>

<style lang="scss">
.video-preview-modal-content {
  border-radius: 12px;
  overflow: hidden;

  .modal-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;

    .modal-title {
      font-weight: 600;
      font-size: 1.1rem;
    }
  }

  .modal-body {
    padding: 1.5rem;
  }
}
</style>
